import { useEffect, useState } from 'react';

/**
 * Scene 02 technical callouts, laid out in screen space around the valve's projected frame
 * (same numbers as the Scene 02 frame in Hero: centre 0.74w / 0.52h, height 0.6h).
 * Labels live only in the free bands above and below that frame, with ≥24px clearance.
 * If a band is too small, its callouts are dropped instead of colliding.
 */
const SLOT_RATIO = 1214 / 1295;
const CLEAR = 24;
const LABEL_W = 168;
const LABEL_H = 46;
const NAV_SAFE = 88;
const GAP = 20;

type Item = { title: string; sub: string };

const TOP: Item[] = [
  { title: 'Handwheel operation', sub: 'Manual control via the top handwheel' },
  { title: 'Durable cast body', sub: 'Robust body for industrial service' },
];
const BOTTOM: Item[] = [
  { title: 'Flange interface', sub: 'Machined faces for pipeline connection' },
  { title: 'Flow control', sub: 'On / off isolation of the line' },
];

type Placed = Item & { x: number; labelY: number; dotY: number; band: 't' | 'b' };

function layout(w: number, h: number): Placed[] {
  const vh = h * 0.6;
  const vw = vh * SLOT_RATIO;
  const box = { l: w * 0.74 - vw / 2, r: w * 0.74 + vw / 2, t: h * 0.52 - vh / 2, b: h * 0.52 + vh / 2 };
  const minX = Math.max(w * 0.47, box.l - 60);
  const maxX = w - 24 - LABEL_W;
  const fit = (n: number) => {
    let k = n;
    while (k > 0 && k * LABEL_W + (k - 1) * GAP > maxX + LABEL_W - minX) k -= 1;
    return k;
  };
  const place = (items: Item[], band: 't' | 'b'): Placed[] => {
    const room = band === 't' ? box.t - CLEAR - NAV_SAFE : h - 20 - (box.b + CLEAR);
    if (room < LABEL_H) return [];
    const k = fit(items.length);
    if (k === 0) return [];
    const span = maxX - minX;
    const step = k > 1 ? span / (k - 1) : 0;
    return items.slice(0, k).map((it, i) => {
      const x = k > 1 ? minX + step * i : (minX + maxX) / 2;
      return band === 't'
        ? { ...it, band, x, labelY: box.t - CLEAR - LABEL_H, dotY: box.t + vh * 0.1 }
        : { ...it, band, x, labelY: box.b + CLEAR, dotY: box.b - vh * 0.1 };
    });
  };
  return [...place(TOP, 't'), ...place(BOTTOM, 'b')];
}

export default function SceneCallouts() {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const update = () => setSize({ w: window.innerWidth, h: Math.max(window.innerHeight, 640) });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  if (!size.w) return null;
  const items = layout(size.w, size.h);

  return (
    <>
      {items.map((c) => {
        const lineTop = c.band === 't' ? c.labelY + LABEL_H + 4 : c.dotY + 4;
        const lineBottom = c.band === 't' ? c.dotY - 4 : c.labelY - 4;
        return (
          <div key={c.title}>
            <span
              className="absolute w-px bg-foreground-950/35"
              style={{ left: c.x, top: lineTop, height: Math.max(0, lineBottom - lineTop) }}
            />
            <span
              className="absolute w-[7px] h-[7px] rounded-full border border-primary-500 bg-background-50"
              style={{ left: c.x - 3, top: c.dotY - 3 }}
            />
            <div className="absolute pl-3 border-l border-primary-500/60" style={{ left: c.x, top: c.labelY, width: LABEL_W, height: LABEL_H }}>
              <p className="font-label text-[10px] tracking-[0.18em] uppercase text-foreground-950 whitespace-nowrap">{c.title}</p>
              <p className="mt-1 text-[11px] leading-snug text-foreground-700">{c.sub}</p>
            </div>
          </div>
        );
      })}
    </>
  );
}